import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, UtensilsCrossed, BedDouble, Plus, Minus } from "lucide-react";

export default function JourneyItinerary({ days = [], dayLabel = "Day" }) {
  const [openSet, setOpenSet] = useState(() => new Set([0]));
  const allOpen = openSet.size === days.length;

  const toggle = (i) =>
    setOpenSet((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });

  const toggleAll = () =>
    setOpenSet(allOpen ? new Set() : new Set(days.map((_, i) => i)));

  return (
    <div data-testid="journey-itinerary-days">
      <div className="flex justify-end mb-2">
        <button
          type="button"
          onClick={toggleAll}
          data-testid="itinerary-toggle-all"
          className="inline-flex items-center gap-1.5 text-[0.65rem] font-semibold uppercase tracking-[0.16em] text-ptf-secondary hover:text-ptf-primary transition-colors"
        >
          {allOpen ? <Minus className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
          {allOpen ? "Close All Days" : "Open All Days"}
        </button>
      </div>
      <div className="border-t border-ptf-primary/12">
        {days.map((d, i) => {
          const open = openSet.has(i);
          return (
            <div key={`${d.title}-${i}`} className="border-b border-ptf-primary/12">
              <button
                type="button"
                onClick={() => toggle(i)}
                data-testid={`itinerary-day-${i + 1}`}
                className="w-full flex items-center justify-between gap-4 py-5 text-left group"
              >
                <span className="flex items-baseline gap-3 min-w-0">
                  <span className="font-display text-sm text-ptf-secondary shrink-0">{dayLabel} {i + 1}</span>
                  <span className="font-display uppercase text-lg md:text-xl tracking-tight text-ptf-primary truncate group-hover:text-ptf-secondary transition-colors">{d.title}</span>
                </span>
                <ChevronDown className={`h-4 w-4 shrink-0 text-ptf-secondary transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence initial={false}>
                {open && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className="overflow-hidden"
                  >
                    <div className="pb-6 pl-0 md:pl-14">
                      {d.description && <p className="text-ptf-text/80 leading-relaxed whitespace-pre-line">{d.description}</p>}
                      {(d.meals || d.stay) && (
                        <div className="mt-4 space-y-1.5">
                          {d.meals && (
                            <p className="flex items-start gap-2 text-sm text-ptf-text/70">
                              <UtensilsCrossed className="h-4 w-4 mt-0.5 shrink-0 text-ptf-secondary" />
                              <span><span className="font-semibold text-ptf-primary">Meals included:</span> {d.meals}</span>
                            </p>
                          )}
                          {d.stay && (
                            <p className="flex items-start gap-2 text-sm text-ptf-text/70">
                              <BedDouble className="h-4 w-4 mt-0.5 shrink-0 text-ptf-secondary" />
                              <span><span className="font-semibold text-ptf-primary">Accommodation:</span> {d.stay}</span>
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
}
